import { format } from 'date-fns';
import { kebabCase, snakeCase, startCase } from 'lodash-es';
import { FLSA_SECTIONS } from '@/helpers/constants/flsaQuestions';
import { DISPLAY_DATE_FORMAT, parseDate } from '@/helpers/date';

export const jurisdictionLabel = (jurisdiction) => {
  if (!jurisdiction) return '';

  return startCase(String(jurisdiction).replace(/_/g, ' '));
};

export const jurisdictionStatusKey = (jurisdiction) => {
  const key = snakeCase(jurisdiction);

  return {
    federal: 'flsa_federal',
    washington: 'flsa_washington',
    california: 'flsa_california',
    new_york: 'flsa_new_york',
    alaska: 'flsa_alaska',
    colorado: 'flsa_colorado',
    massachusetts: 'flsa_massachusetts',
  }[key] || 'flsa_default';
};

export const formatFlsaSubmissionDate = (createdAt) => {
  try {
    return format(parseDate(createdAt), DISPLAY_DATE_FORMAT);
  } catch {
    return '';
  }
};

export const formatFlsaSubmissionTime = (createdAt) => {
  try {
    return format(parseDate(createdAt), 'h:mm a');
  } catch {
    return '';
  }
};

export const formatFlsaFormYearLabel = (year) => (year ? `${year} Form` : '');

export const getEffectiveFlsaForms = (forms = []) => (
  forms.filter((form) => form?.is_effective)
);

// TODO: Update all this once the API returns the disabled jurisdictions.
export const FLSA_OPTIONAL_DISABLED_JURISDICTIONS = [
  'alaska',
  'california',
  'colorado',
  'massachusetts',
  'new_york',
  'washington',
];

export const getRealFlsaFormUlids = (forms = []) => (
  new Set(getEffectiveFlsaForms(forms).map((form) => form.ulid))
);

export const buildFlsaFormSelectItems = (forms = []) => {
  const effectiveForms = getEffectiveFlsaForms(forms);

  const apiItems = effectiveForms.map((form) => ({
    title: jurisdictionLabel(form.jurisdiction),
    value: form.ulid,
    props: { disabled: false },
  }));

  const apiJurisdictions = new Set(
    effectiveForms.map((form) => snakeCase(form.jurisdiction)),
  );

  const disabledPlaceholders = FLSA_OPTIONAL_DISABLED_JURISDICTIONS
    .filter((jurisdiction) => !apiJurisdictions.has(jurisdiction))
    .map((jurisdiction) => ({
      title: jurisdictionLabel(jurisdiction),
      value: jurisdiction,
      props: { disabled: true },
    }));

  return [...apiItems, ...disabledPlaceholders];
};

export const navigateToFlsaFormCreate = ({
  router,
  jobProfileUlid,
  draftJobProfileUlid,
  flsaFormUlid,
  realFormUlids,
}) => {
  if (
    (!jobProfileUlid && !draftJobProfileUlid)
    || !realFormUlids?.has(flsaFormUlid)
  ) {
    return;
  }

  const query = jobProfileUlid
    ? { hrisUlid: jobProfileUlid, flsaFormUlid }
    : { draftUlid: draftJobProfileUlid, flsaFormUlid };

  router.push({ name: 'flsa-form-create', query });
};

export const findEffectiveFlsaForm = (forms, { ulid, jurisdiction } = {}) => {
  const effective = getEffectiveFlsaForms(forms);

  if (ulid) return effective.find((form) => form.ulid === ulid);
  if (jurisdiction) return effective.find((form) => form.jurisdiction === jurisdiction);

  return null;
};

// Returns the profile ULIDs to use when loading or creating FLSA submissions.
// Linked drafts and HRIS profiles share the HRIS profile's submissions.
export const getFlsaSubmissionProfileUlids = ({ jobProfile, isHrisProfile }) => {
  if (!jobProfile?.ulid) {
    return { jobProfileUlid: null, draftJobProfileUlid: null, jobProfileVersionUlid: null };
  }

  const hrisUlid = isHrisProfile
    ? jobProfile.ulid
    : jobProfile.hris_reference_ulid;

  if (hrisUlid) {
    return {
      jobProfileUlid: hrisUlid,
      draftJobProfileUlid: null,
      jobProfileVersionUlid: isHrisProfile
        ? jobProfile.last_version_ulid || null
        : jobProfile.hris_reference?.last_version_ulid || null,
    };
  }

  return {
    jobProfileUlid: null,
    draftJobProfileUlid: jobProfile.ulid,
    jobProfileVersionUlid: null,
  };
};

const buildFormYearByUlid = (forms = []) => Object.fromEntries(
  forms.filter((form) => form?.ulid && form?.year).map((form) => [form.ulid, form.year]),
);

export const attachFormYears = (submissions, forms) => {
  const byUlid = buildFormYearByUlid(forms);

  return submissions.map((submission) => ({
    ...submission,
    formYear: byUlid[submission.flsa_form_ulid],
  }));
};

export const sortSubmissionsByDateDesc = (submissions) => {
  const list = [...submissions];

  list.sort((a, b) => {
    const timeA = parseDate(a.created_at)?.getTime() || 0;
    const timeB = parseDate(b.created_at)?.getTime() || 0;

    return timeB - timeA;
  });

  return list;
};

// Groups submissions by jurisdiction and sorts them by date in descending order.
export const groupSubmissionsByJurisdiction = (submissions) => {
  const grouped = new Map();

  submissions.forEach((submission) => {
    const key = snakeCase(submission.jurisdiction || '');

    if (!key) return;

    const existing = grouped.get(key) || [];

    grouped.set(key, [...existing, submission]);
  });

  grouped.forEach((items, key) => {
    grouped.set(key, sortSubmissionsByDateDesc(items));
  });

  return grouped;
};

// Converts a submission object from the API to a form state object.
export const submissionToFormState = (submission) => {
  if (!submission) {
    return {
      determination: null,
      primaryExemptionBasis: null,
      notesText: '',
      answers: {},
    };
  }

  const determination = submission.is_exempt ? 'exempt' : 'non-exempt';

  const primaryExemptionBasis = submission.primary_exemption_category
    ? kebabCase(submission.primary_exemption_category)
    : null;

  const notesText = submission.data_json?.notes || '';

  const flat = submission.data_json || {};
  const answers = {};

  FLSA_SECTIONS.forEach((section) => {
    section.groups.forEach((group) => {
      group.questions.forEach((question) => {
        const val = flat[question.id];

        if (typeof val === 'boolean') {
          answers[`${section.id}:${question.id}`] = val;
        }
      });
    });
  });

  return {
    determination,
    primaryExemptionBasis,
    notesText,
    answers,
  };
};

// Converts a form state object to an object for the API.
export const formStateToDataJson = ({
  determination,
  primaryExemptionBasis,
  notesText,
  answers,
}) => {
  const questionAnswers = Object.fromEntries(
    Object.entries(answers)
      .map(([key, value]) => [key.split(':').slice(1).join(':'), value]),
  );

  let overall = null;

  if (determination === 'exempt') {
    overall = true;
  } else if (determination === 'non-exempt') {
    overall = false;
  }

  const payload = {
    ...questionAnswers,
  };

  if (determination === 'exempt' && primaryExemptionBasis) {
    payload.primary_exemption_category = snakeCase(primaryExemptionBasis);
  }

  if (notesText?.trim()) {
    payload.notes = notesText;
  }

  if (overall !== null) {
    payload.overall = overall;
  }

  return payload;
};
